'use client';

import { useEffect } from 'react';

/**
 * Keep the assistant usable while a phone keyboard is on screen.
 *
 * Two things happen for as long as the overlay is open. The viewport meta gets
 * `interactive-widget=resizes-content`, which asks the browser to shrink the
 * layout viewport around the keyboard instead of sliding the page up behind it
 * — without it, Chrome on Android leaves the fixed input bar and the bottom of
 * the answer underneath the keys. And the part of the viewport the keyboard
 * still covers is published as `--chatbot-keyboard-inset`: the input bar sits on
 * top of it and the conversation stops above it, while the frosted backdrop
 * keeps covering the screen behind it (see _chatbot.scss). On iOS, where the
 * layout viewport never shrinks, that variable is what lifts them clear.
 *
 * The zoom is pinned at the same time: the overlay is the whole screen on a
 * phone, and a pinch (or the focus zoom Safari does on a sub-16px field) leaves
 * the visitor panning a chat whose edges they can't see. Everything is undone
 * on close, so the rest of the site stays zoomable.
 *
 * @param {boolean} active
 * @param {() => void} [onViewportChange] run after every keyboard/viewport change
 */
export default function useOverlayViewport(active, onViewportChange) {
  useEffect(() => {
    if (!active) return undefined;

    const meta = document.querySelector('meta[name="viewport"]');
    const previous = meta?.getAttribute('content') ?? null;
    meta?.setAttribute(
      'content',
      `${previous || 'width=device-width, initial-scale=1'}, maximum-scale=1, user-scalable=no, interactive-widget=resizes-content`
    );

    const root = document.documentElement;
    const viewport = window.visualViewport;

    const applyGeometry = () => {
      // How much of the layout viewport the keyboard still covers. Where the
      // browser already shrank the layout viewport this comes out at 0, so the
      // two behaviours can't stack up.
      const covered = viewport ? window.innerHeight - viewport.height - viewport.offsetTop : 0;
      // Under a couple of pixels is rounding noise, not a keyboard — Firefox on
      // a desktop reports a 1px difference and nothing is covering anything.
      const inset = covered < 2 ? 0 : Math.round(covered);
      root.style.setProperty('--chatbot-keyboard-inset', `${inset}px`);
      // Where the visible part of the page starts inside the layout viewport.
      // With the document pinned (see Chatbot.jsx) Safari can no longer scroll
      // the field into view, so it shifts the visual viewport instead — and a
      // `position: fixed` overlay, which is laid out against the *layout*
      // viewport, has to be moved down by the same amount or it hangs off the
      // top of the screen with the page showing below it.
      const shifted = viewport ? viewport.offsetTop : 0;
      root.style.setProperty('--chatbot-viewport-top', `${Math.max(0, Math.round(shifted))}px`);
      onViewportChange?.();
    };

    applyGeometry();
    viewport?.addEventListener('resize', applyGeometry);
    viewport?.addEventListener('scroll', applyGeometry);
    window.addEventListener('resize', applyGeometry);
    window.addEventListener('orientationchange', applyGeometry);

    // The first open measures mid-flight. On iOS the keyboard slides up while
    // Safari collapses its bottom toolbar to the url pill, and a reading taken
    // between the two counts the toolbar as keyboard — around 110px too much,
    // which lifts the input bar clear of the bottom of the screen. If the
    // viewport settles without firing another event, that reading is what
    // sticks; opening a second time is fine only because the toolbar is
    // already collapsed by then. So re-measure across both animations.
    const settling = [60, 180, 350, 600, 900].map((ms) => setTimeout(applyGeometry, ms));

    return () => {
      settling.forEach(clearTimeout);
      viewport?.removeEventListener('resize', applyGeometry);
      viewport?.removeEventListener('scroll', applyGeometry);
      window.removeEventListener('resize', applyGeometry);
      window.removeEventListener('orientationchange', applyGeometry);
      root.style.removeProperty('--chatbot-keyboard-inset');
      root.style.removeProperty('--chatbot-viewport-top');
      if (meta && previous !== null) meta.setAttribute('content', previous);
    };
  }, [active, onViewportChange]);
}
